import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    styled,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';

const StyledCard = styled(Card)({
    marginBottom: "24px",
    border: "1px solid rgba(0, 0, 0, 0.04)",
    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.01)",
    borderRadius: "20px",
    backgroundColor: "#FFFFFF",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.08)",
        "&:before": {
            opacity: 1,
        },
    },
    "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "4px",
        height: "100%",
        backgroundColor: "#2563EB",
        opacity: 0,
        transition: "opacity 0.3s ease-in-out",
    },
});

const StyledCardContent = styled(CardContent)({
    padding: "32px",
    "&:last-child": {
        paddingBottom: "32px",
    },
});

const LabelText = styled(Typography)({
    fontSize: "13px",
    color: "#64748B",
    marginBottom: "8px",
    fontWeight: 500,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    opacity: 0.9,
});

const ValueText = styled(Typography)({
    fontSize: "14px",
    color: "#1E293B",
    fontWeight: 600,
});

const ViewButton = styled(Button)({
    backgroundColor: "#2563EB",
    "&:hover": { backgroundColor: "#1D4ED8" },
});

const EmptyState = () => (
    <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
        <Typography variant="h6" paragraph>
            No proposals found
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
            There are no proposals to display
        </Typography>
    </Box>
);

const ProposalCard = React.memo(({ proposal, onViewDetails, onViewApplication, showLenderId }) => {
    const fields = [
        { label: "Proposal ID", value: proposal.proposalId },
        { label: "Created By", value: proposal.createOpName || "System" },
        { label: "Created Date", value: proposal.submittedDate },
        { label: "Status", value: proposal.status.replace(/_/g, " "), capitalize: true },
        { label: "Application ID", value: proposal.applicationId },
        { label: "User ID", value: proposal.userId || "N/A" },
        { label: "Borrower ID", value: proposal.borrowerId || "N/A" },
        ...(showLenderId ? [{ label: "Lender ID", value: proposal.lenderId || "N/A" }] : []),
    ];

    return (
        <StyledCard>
            <StyledCardContent>
                <Grid container spacing={3}>
                    {fields.map(({ label, value, capitalize }, index) => (
                        <Grid item xs={12} sm={6} md={3} key={label}>
                            <LabelText>{label}</LabelText>
                            <ValueText sx={capitalize ? { textTransform: "capitalize" } : undefined}>
                                {value}
                            </ValueText>
                        </Grid>
                    ))}
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                            {onViewApplication && (
                                <Button
                                    variant="outlined"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => onViewApplication(proposal)}
                                    sx={{ textTransform: "none" }}
                                >
                                    View Application
                                </Button>
                            )}
                            <ViewButton
                                variant="contained"
                                startIcon={<VisibilityIcon />}
                                onClick={() => onViewDetails(proposal)}
                            >
                                View Details
                            </ViewButton>
                        </Box>
                    </Grid>
                </Grid>
            </StyledCardContent>
        </StyledCard>
    );
});

const ProposalList = ({
    proposals = [],
    onViewDetails,
    onViewApplication,
    groupedByMonth = false,
    showLenderId = false
}) => {
    const groupedProposals = useMemo(() => {
        if (!groupedByMonth || !Array.isArray(proposals)) return null;

        const groups = proposals.reduce((acc, proposal) => {
            const date = new Date(proposal.submittedDate);
            const monthYear = date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
            (acc[monthYear] = acc[monthYear] || []).push(proposal);
            return acc;
        }, {});

        return Object.entries(groups)
            .sort(([a], [b]) => new Date(b) - new Date(a))
            .map(([monthYear, monthProposals]) => ({
                monthYear,
                proposals: monthProposals
            }));
    }, [proposals, groupedByMonth]);

    if (proposals.length === 0) return <EmptyState />;

    if (!groupedByMonth) {
        return (
            <Box>
                {proposals.map(proposal => (
                    <ProposalCard
                        key={proposal.id}
                        proposal={proposal}
                        onViewDetails={onViewDetails}
                        onViewApplication={onViewApplication}
                        showLenderId={showLenderId}
                    />
                ))}
            </Box>
        );
    }

    return (
        <Box>
            {groupedProposals.map(({ monthYear, proposals }) => (
                <Box key={monthYear} sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        {monthYear}
                    </Typography>
                    {proposals.map(proposal => (
                        <ProposalCard
                            key={proposal.id}
                            proposal={proposal}
                            onViewDetails={onViewDetails}
                            onViewApplication={onViewApplication}
                            showLenderId={showLenderId}
                        />
                    ))}
                </Box>
            ))}
        </Box>
    );
};

export default React.memo(ProposalList); 